// libraries
import { dealerSelectors } from '@makemydeal/dr-shared-store';

// selectors
import * as configSelectors from '../selectors/config';
import { getGlobalPrintingSettingsHasFetched } from '../selectors/globalPrintingSettingsSelectors';

// actions
import { fetchPrintingLogo } from '../actions/globalPrintingSettingsActionCreators';

// interfaces/types
import type { ReduxStore } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';

/**
 * Guarantees the dealer printing logo is in state before a print payload is built.
 *
 * The logo is no longer fetched on page initialization (US2056456), so every flow that puts
 * `dealerLogo` on a payload — print/text/email, publish to showroom, generate-pdf, proposal
 * preview — must go through here first. Resolves immediately once the logo has been fetched.
 *
 * A failed fetch never blocks printing: redux-api-middleware turns network/HTTP errors into
 * `FETCH_PRINTING_LOGO_FAILURE` and resolves, and that action marks the fetch as attempted, so the
 * PDF is simply produced without a logo.
 */
export const ensurePrintingLogo = async (store: ReduxStore<StateTree>): Promise<void> => {
    const state = store.getState();

    if (getGlobalPrintingSettingsHasFetched(state)) return;

    const dealerId = dealerSelectors.getDealerId(state);
    const bffEndpoint = configSelectors.getServicesBff(state);

    if (dealerId <= 0 || !bffEndpoint) return;

    // `dispatch` is typed as returning the action, but redux-api-middleware returns a promise that
    // settles when the request completes, so the cast is what makes this awaitable.
    await (store.dispatch(fetchPrintingLogo(dealerId, bffEndpoint)) as unknown as Promise<void>);
};
